<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { derived as sderived } from 'svelte/store'

    import type { GenOrder } from '../../order'
    import Icon from '@iconify/svelte'
    import { toUnicode } from 'punycode-esm'

    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'
    import { DateTime, toLuxon } from 'hydria/utils/time'

    import Date from 'hydria/components/Date.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const Order = getContext('Order') as GenOrder
    const status = sderived(Order.status, ($status) => $status)
    const items = sderived(Order.items, ($items) => $items)
    const order = sderived(Order.orderStore, ($order) => $order)

    interface PubZoneCandidate {
        idna: string
        unicode: string
        domainExpiration: DateTime
        zoneExpiration: DateTime
        domainId: string
    }
    let zoneList: PubZoneCandidate[] = $state([])
    // let zoneListIndex: { [key: string]: PubZoneCandidate } = {}
    // $: if (zoneList != null) zoneListIndex = Object.fromEntries(zoneList.map((z) => [z.idna, z]))

    let zonePackPrices = {
        current: [],
        exp: [],
    }

    let zoneLimits: any = $state()

    let zonesInOrder: Set<string> = $state(new Set())
    let newDomainsWithoutZoneInOrder: Set<string> = $state(new Set())

    let loadingLimits = $state(false)

    function cleanupPeriod(period: any) {
        if (!period) return null
        if (period.start_date) period.start_date = toLuxon(period.start_date)
        if (period.end_date) period.end_date = toLuxon(period.end_date)
        return period
    }

    async function loadLimits() {
        loadingLimits = true
        let r: SimpleFetchResult

        try {
            r = await simpleFetch('GET', `/json/ecom/1/zone-ecom-limits/${$order.owner}`)
        } catch (e) {
            console.error('Failed to load limits:', e)
            notify({
                type: 'danger',
                title: 'Limites des zones (“slots”)',
                text: 'L’information sur les zones n’a pas pu être chargée.',
            })
            return
        }

        const l = r.data.limits
        l.now = toLuxon(l.now)
        if (l.exp_time) l.exp_time = toLuxon(l.exp_time)
        l.current_period = cleanupPeriod(l.current_period)
        l.exp_period = cleanupPeriod(l.exp_period)

        // Also includes following keys:
        // - total_slots_admin
        // - total_slots_at_exp
        // - total_slots_now
        // Periods include:
        // - n_days (Feb 29 skipped)
        // - n_real_days (Feb 29 included)
        // - price_per_block: cents for this blocks,
        //                    essentially n_days/365*full_length_price
        //                    Needs to be multiplied by the number of blocks the user wants
        l.abs_total_slots_now = l.total_slots_admin + l.total_slots_now
        l.abs_total_slots_at_exp = l.total_slots_admin + l.total_slots_at_exp

        for (const periodTag of ['current', 'exp']) {
            zonePackPrices[periodTag].length = 0
            const period = l[`${periodTag}_period`]
            if (!period) continue

            for (let n = 1; n <= 5; n++) {
                let nZones = n * 1 // FIXME: fetch "*1" de la DB comme pour le prix
                let price = n * period.price_per_block
                let o = price.toString()
                if (o.length == 1) o = '00' + o
                else if (o.length == 2) o = '0' + o
                let optionLine1 = $ngettext('{n} zone for ${dollars}.{cents}', '{n} zones for ${dollars}.{cents}', { n: nZones, dollars: o.slice(0, -3), cents: o.slice(-2) })
                let selectedText = $ngettext('{n} zone', '{n} zones', { n: nZones })
                let optionLine2: string
                if (periodTag == 'current') {
                    optionLine2 = $ngettext('{n} day from today', '{n} days from today', { n: period.n_real_days })
                } else {
                    if (l.exp_time) {
                        optionLine2 = $ngettext('{n} day from {start}', '{n} days from {start}', { n: period.n_real_days, start: l.exp_time.toLocaleString(DateTime.DATE_MED) })
                    } else {
                        optionLine2 = $ngettext('{n} day from {start}', '{n} days', { n: period.n_real_days })
                    }
                }

                zonePackPrices[periodTag].push({
                    nSlots: nZones,
                    price: price,
                    startNow: periodTag == 'current',
                    optionLine1: optionLine1,
                    optionLine2: optionLine2,
                    selectedText: selectedText,
                })
            }

            zoneLimits = l
        }
        zonePackPrices = zonePackPrices

        loadingLimits = false
    }

    let loadingPossibleZones = $state(false)

    async function loadPossibleZones() {
        loadingPossibleZones = true
        let r: SimpleFetchResult

        try {
            r = await simpleFetch('GET', `/json/ecom/1/possible-publishable-zones/${$order.owner}`)
        } catch (e) {
            console.error('Failed to load possible zones:', e)
            notify({
                type: 'danger',
                title: 'Zones possibles',
                text: 'L’information sur les zones possiblement publiables n’a pas pu être chargée.',
            })
            return
        }

        const lst: PubZoneCandidate[] = []
        for (const d of r.data.domains) {
            if (d.expiration) d.expiration = toLuxon(d.expiration)
            if (d.zone_expiration) d.zone_expiration = toLuxon(d.zone_expiration)
            lst.push({
                idna: d.idna,
                unicode: toUnicode(d.idna),
                domainExpiration: d.expiration,
                zoneExpiration: d.zone_expiration,
                domainId: d.id,
            })
        }
        zoneList = lst
        loadingPossibleZones = false
    }

    unsubs.push(
        status.subscribe(($status) => {
            if ($status == 'ready') {
                zonesInOrder = new Set($items.filter((i) => i.type == 'PZONE').map((i) => i.meta.a_name))
                newDomainsWithoutZoneInOrder = new Set($items.filter((i) => i.type == 'DOMAIN' && !zonesInOrder.has(i.meta.a_name)).map((i) => i.meta.a_name))

                loadLimits()
                loadPossibleZones()
            }
        }),
    )

    // $: console.log({ zonePackPrices })
    // $: console.log({ zoneList })

    let adding: false | string = $state(false)
    async function addZone(item: any) {
        adding = item.idna
        await Order.addZone(typeof item === 'string' ? item : item.idna, 1)
        adding = false
    }

    async function addZoneSlots(nSlots: number | null, startNow: boolean = false) {
        adding = 'zones'
        await Order.addZoneSlots(nSlots, startNow)
        adding = false
    }
</script>

<div id="order-zones">
    {#if loadingLimits}
        <LoadingPattern pattern={['x', 'x']} />
    {:else}
        <ul>
            <li>{$ngettext('{n} published zone out of {total}', '{n} published zones out of {total}', { n: zoneLimits.published_count, total: zoneLimits.zone_count })}</li>
            {#if !zoneLimits.exp_time}
                <li>{$ngettext('Can have up to {n} published zone', 'Can have up to {n} published zones', { n: zoneLimits.abs_total_slots_now })}</li>
            {:else}
                <li>{$ngettext('Can have up to {n} published zone until {date}', 'Can have up to {n} published zones until {date}', { n: zoneLimits.abs_total_slots_now, date: zoneLimits.exp_time.toLocaleString(DateTime.DATE_MED) })}</li>
                <li>{$ngettext('Can have up to {n} published zone after {date}', 'Can have up to {n} published zones after {date}', { n: zoneLimits.abs_total_slots_at_exp, date: zoneLimits.exp_time.toLocaleString(DateTime.DATE_MED) })}</li>
            {/if}
        </ul>
        {#if zoneLimits.exp_time}
            <p><button type="button" class="btn btn-outline-secondary" onclick={() => addZoneSlots(null, true)} disabled={!!adding}>Ajouter des zones à la <em>période courrante</em></button></p>
            <p><button type="button" class="btn btn-outline-secondary" onclick={() => addZoneSlots(null, false)} disabled={!!adding}>Ajouter des zones à la <em>prochaine période</em></button></p>
        {:else}
            <p><button type="button" class="btn btn-outline-secondary" onclick={() => addZoneSlots(null, true)} disabled={!!adding}>Ajouter la possibilité de publier des zones arbitraires</button></p>
        {/if}
    {/if}

    {#if newDomainsWithoutZoneInOrder.size}
        <p><strong>Domaines dans la commande :</strong></p>
        <div style="display: flex; flex-wrap: wrap; gap: .5ex;">
            {#each [...newDomainsWithoutZoneInOrder] as aname}
                <button type="button" class="btn btn-outline-secondary" onclick={() => addZone(aname)}>{toUnicode(aname)}</button>
            {/each}
        </div>
    {/if}

    {#if loadingPossibleZones}
        <LoadingPattern pattern={['x', 'x', 'x']} />
    {:else}
        <div class="renewal-list-wrapper">
            {#if zoneList.length < 1}
                <em>Aucune zone</em>
            {:else}
                <div class="renewal-order">
                    <table class="table">
                        <thead>
                            <tr>
                                <th>{$_('Domain')}</th>
                                <th class="text-center">Dom Expiration</th>
                                <th class="text-center">Zone Expiration</th>
                                <th class="text-center">+</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each zoneList as item}
                                <tr>
                                    <td><a use:sanchor href={spath(`/domain/${item.idna}`)}>{item.unicode}</a></td>
                                    <td class="text-center"><Date value={item.domainExpiration} format="h-table-s" /></td>
                                    <td class="text-center"><Date value={item.zoneExpiration} format="h-table-s" /></td>
                                    <td class="text-center">
                                        <button type="button" class="btn btn-sm btn-outline-secondary" class:btn-info={adding == item.idna} disabled={!!adding || zonesInOrder.has(item.idna)} onclick={() => addZone(item)}>
                                            {#if adding == item.idna}
                                                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                            {:else if zonesInOrder.has(item.idna)}
                                                <Icon inline icon="fa:minus" />
                                            {:else}
                                                <Icon inline icon="fa:plus" />
                                            {/if}
                                        </button>
                                    </td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>
            {/if}
        </div>
    {/if}
</div>
